<template>
	<Application
		name="OIDC Callback"
		:loading="processing"
		v-model:error-title="errorTitle"
		:error-detail="errorDetail"
		class="oidcCallback"
	>
		<template #navbar></template>
		<v-container class="oidcCallback__container">
			<div v-if="error" class="oidcCallback__errorCard">
				<v-icon color="error" size="48" class="oidcCallback__icon">mdi-alert-circle</v-icon>
				<p class="oidcCallback__message">{{ error }}</p>
				<v-btn
					variant="flat"
					class="gradientButton retryButton oidcCallback__button"
					@click="retryLogin"
				>
					{{ $t('text.retry') }}
				</v-btn>
			</div>
		</v-container>
	</Application>
</template>

<script>
import Application from '@/views/applications/Application.vue'
import Common from '@/mixins/Common.vue'
import Authorization from '@/mixins/Authorization.vue'
import { translationUtil } from '@/utils/translation-util.js'
import { getUserManager } from './oidc-keycloak'
import { getLoginConfig } from './login-config'

// oidc-client-ts has no error code for this, only the message
const MISSING_STATE_ERROR = 'No matching state found in storage'
// sessionStorage, so the guard is per tab and survives the keycloak round trip
const MISSING_STATE_RETRY_KEY = 'mysOidcMissingStateRetry'

/*
 * MYS-7381: diagnostic for a callback whose state is not in localStorage. No states at
 * all means the callback landed in another browser storage than the one the login was
 * started from (e.g. Edge moving the Microsoft login into the work profile); other
 * states mean ours was replaced. Only ids and ages - the entries hold the PKCE verifier.
 */
function describeStoredOidcStates() {
	const now = Date.now() / 1000
	return Object.keys(localStorage)
		.filter(key => key.startsWith('oidc.') && !key.startsWith('oidc.user:'))
		.map(key => {
			try {
				const state = JSON.parse(localStorage.getItem(key))
				return { id: state.id, ageSeconds: Math.round(now - state.created), requestType: state.request_type }
			}
			catch (e) {
				return { key }
			}
		})
}

export default {
	name: 'OAuthCallback',
	components: { Application },
	mixins: [Common, Authorization],
	data() {
		return {
			processing: true,
			error: null,
		}
	},
	methods: {
		async retryLogin() {
			this.error = null
			this.processing = true
			try {
				// keep the identity provider hint on retry, otherwise an SSO managed
				// client would land on the keycloak password form instead (MYS-7187)
				const { idpHint } = getLoginConfig()
				await getUserManager().signinRedirect(idpHint ? { extraQueryParams: { kc_idp_hint: idpHint } } : undefined)
			} catch (e) {
				this.error = e.message || 'Failed to initiate login'
				this.processing = false
			}
		},
		async processCallback() {
			try {
				const user = await getUserManager().signinCallback(window.location.href)

				if (!user) {
					this.error = 'No user data received from login provider'
					this.processing = false
					return
				}

				sessionStorage.removeItem(MISSING_STATE_RETRY_KEY)

				// Call doLogin with the token from the OIDC response
				const ok = await this.login('TOKEN', user.access_token, user.refresh_token)
				this.processing = false

				if (ok) {
					// Get the initial URL that was stored before login
					const initialUrl = this.$store.state.initialUrl

					// Reload translations
					translationUtil.reload(this.$store)

					// Navigate to initial URL or dashboard
					if (initialUrl && initialUrl !== '') {
						window.location.href = initialUrl
					} else {
						this.$router.push('/dashboard')
					}
				}
			} catch (e) {
				console.error('OIDC callback error:', e)

				if (e?.message === MISSING_STATE_ERROR) {
					console.warn('OIDC callback state', new URLSearchParams(location.search).get('state'),
						'not found, stored states:', describeStoredOidcStates())

					// MYS-7381: by now keycloak (and the identity provider) hold a session, so
					// a fresh redirect comes straight back with a state we do have - this is
					// what the retry button does, just without the user. Once per tab, so a
					// callback that keeps losing its state shows the error instead of looping.
					if (!sessionStorage.getItem(MISSING_STATE_RETRY_KEY)) {
						sessionStorage.setItem(MISSING_STATE_RETRY_KEY, 'true')
						await this.retryLogin()
						return
					}
				}

				// re-arm the automatic retry for the next attempt
				sessionStorage.removeItem(MISSING_STATE_RETRY_KEY)
				this.error = e.message || 'Authentication failed'
				this.processing = false
			}
		},
	},
	async mounted() {
		await this.processCallback()
	},
}
</script>

<style scoped>
.oidcCallback {
	min-height: 100vh;
	background-image: url('/myservices_background.jpg');
	background-size: cover !important;
	background-position: top center;
}

.oidcCallback__container {
	min-height: 100vh;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 32px 16px;
}

.oidcCallback__errorCard {
	width: min(100%, 440px);
	padding: 40px 32px;
	border-radius: 5px !important;
    padding: 35px 50px !important;
    box-shadow: 0 0 15px rgba(0, 0, 0, 0.2);
	background: #fff;
}

.oidcCallback__icon {
	margin-bottom: 12px;
	margin-left: auto;
	margin-right: auto;
	width: 100% !important;
}

.oidcCallback__message {
	margin: 0 0 24px;
	color: rgb(var(--v-theme-error));
	font-size: 1rem;
	line-height: 1.5;
}

.oidcCallback__button {
	margin-left: auto;
	margin-right: auto;
	margin-top: 12px;
}
</style>
